import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactElement, ReactNode } from 'react';
import { authResult, loginInput, magicLinkConsumeInput, signupInput } from '@xdocs/schemas';
import type { AuthResult, LoginInput, MagicLinkConsumeInput, SignupInput } from '@xdocs/schemas';
import { resolveWsUrl } from '../lib/env.js';
import { requestAuthOp } from './authTransport.js';
import { mintSessionCookie, silentRefresh } from './sessionCookieTransport.js';

export type AuthStatus = 'unauthenticated' | 'authenticating' | 'authenticated';

export interface AuthState {
  status: AuthStatus;
  accessToken: string | null;
  userId: string | null;
}

export interface AuthContextValue extends AuthState {
  login: (input: LoginInput) => Promise<void>;
  signup: (input: SignupInput) => Promise<void>;
  /** Consumes a magic-link token (`/auth/callback?token=…`, F12 path 3) and
   * establishes the session exactly like `login`/`signup` — same
   * bootstrap-cookie mint + in-memory state transition, just a different
   * pre-handshake WS op up front. */
  consumeMagicLink: (input: MagicLinkConsumeInput) => Promise<void>;
  /** Clears the in-memory session. Does NOT call `auth.logout` — the WS
   * layer (`useLogout`, `../ws/WsProvider.tsx`) owns telling the server,
   * since that requires an authenticated `WsClient` this provider doesn't
   * hold a reference to (§ dependency direction: auth state is WS-agnostic
   * so `/login`/`/signup` don't need a live socket to render). */
  clearSession: () => void;
}

/** Exported so a caller can compose a fixed/mock value (e.g. tests
 * exercising `RequireAuth`'s branching without a real WS round-trip)
 * without reaching for a full module mock. */
export const AuthContext = createContext<AuthContextValue | null>(null);

export interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Owns the in-memory access token (E17 §5.2: short-lived, delivered in the
 * `res` payload — NEVER `localStorage`, unlike the refresh token which is a
 * server-set `HttpOnly` cookie this app never touches directly). A hard page
 * reload therefore starts with NO access token in memory, but is NOT
 * unauthenticated: the mount effect below attempts a silent refresh
 * (`sessionCookieTransport.silentRefresh`, `GET /ws?refresh=1`) against the
 * httpOnly cookie before ever settling into `'unauthenticated'` — that's the
 * E20-resume gap this closes. `status: 'authenticating'` covers BOTH that
 * boot-time check and an in-flight login/signup submit; `RequireAuth` treats
 * either the same way (render nothing, no redirect).
 */
export function AuthProvider({ children }: AuthProviderProps): ReactElement {
  const [state, setState] = useState<AuthState>({ status: 'authenticating', accessToken: null, userId: null });
  const wsUrl = useRef(resolveWsUrl()).current;
  /**
   * Single-flight guard for the boot refresh. React `StrictMode` (on in
   * `main.tsx`, so in every dev/e2e run) runs the mount effect TWICE — and
   * two concurrent `?refresh=1` calls are actively destructive with a
   * one-time-use rotating token: both carry the same old cookie, one wins
   * the rotation (200 + fresh cookie), the loser 401s and its `Max-Age=0`
   * clear then WIPES the fresh cookie — logging the user out on the spot
   * (observed as the e2e login-bounce). A ref survives the StrictMode
   * effect re-run, so both invocations share ONE fetch.
   */
  const bootRefresh = useRef<Promise<AuthResult | null> | null>(null);
  /**
   * Set the instant `login`/`signup`/`consumeMagicLink` START (not finish) —
   * guards a real race the auto-triggered `consumeMagicLink` (`/auth/
   * callback?token=…`, fires from a mount effect with NO user interaction to
   * naturally order it after boot) introduced: without this, a slow-to-
   * resolve boot-time silent refresh finishing AFTER a magic-link consume
   * already landed `authenticated` would stomp it back to `unauthenticated`
   * — the boot effect's own resolution below bails out once this is set,
   * ceding the session lifecycle to whichever real flow claimed it first.
   */
  const sessionClaimed = useRef(false);

  useEffect(() => {
    let cancelled = false;
    bootRefresh.current ??= silentRefresh();
    bootRefresh.current
      .then((result) => {
        if (cancelled || sessionClaimed.current) return;
        setState(
          result
            ? { status: 'authenticated', accessToken: result.accessToken, userId: result.userId }
            : { status: 'unauthenticated', accessToken: null, userId: null },
        );
      })
      .catch(() => {
        // silentRefresh already resolves null on every failure mode; this
        // catch only guards against a truly unexpected throw so boot never
        // hangs on 'authenticating' forever.
        if (!cancelled && !sessionClaimed.current) {
          setState({ status: 'unauthenticated', accessToken: null, userId: null });
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  /** Shared by every pre-handshake op that ends in a fresh session
   * (`auth.login`/`auth.signup`/`auth.magicLink.consume`): mint the httpOnly
   * refresh cookie from the access token, then flip in-memory state to
   * `authenticated`. Pulled out so the three call sites (and their identical
   * `unauthenticated`-on-throw recovery) can't drift from each other. */
  const applyAuthResult = useCallback(async (result: AuthResult): Promise<void> => {
    await mintSessionCookie(result.accessToken);
    setState({ status: 'authenticated', accessToken: result.accessToken, userId: result.userId });
  }, []);

  const login = useCallback(
    async (input: LoginInput): Promise<void> => {
      sessionClaimed.current = true;
      setState((prev) => ({ ...prev, status: 'authenticating' }));
      try {
        const parsed = loginInput.parse(input);
        const raw = await requestAuthOp<unknown>(wsUrl, 'auth.login', parsed);
        await applyAuthResult(authResult.parse(raw));
      } catch (err) {
        setState({ status: 'unauthenticated', accessToken: null, userId: null });
        throw err;
      }
    },
    [wsUrl, applyAuthResult],
  );

  const signup = useCallback(
    async (input: SignupInput): Promise<void> => {
      sessionClaimed.current = true;
      setState((prev) => ({ ...prev, status: 'authenticating' }));
      try {
        const parsed = signupInput.parse(input);
        const raw = await requestAuthOp<unknown>(wsUrl, 'auth.signup', parsed);
        await applyAuthResult(authResult.parse(raw));
      } catch (err) {
        setState({ status: 'unauthenticated', accessToken: null, userId: null });
        throw err;
      }
    },
    [wsUrl, applyAuthResult],
  );

  const consumeMagicLink = useCallback(
    async (input: MagicLinkConsumeInput): Promise<void> => {
      sessionClaimed.current = true;
      setState((prev) => ({ ...prev, status: 'authenticating' }));
      try {
        const parsed = magicLinkConsumeInput.parse(input);
        const raw = await requestAuthOp<unknown>(wsUrl, 'auth.magicLink.consume', parsed);
        await applyAuthResult(authResult.parse(raw));
      } catch (err) {
        setState({ status: 'unauthenticated', accessToken: null, userId: null });
        throw err;
      }
    },
    [wsUrl, applyAuthResult],
  );

  const clearSession = useCallback(() => {
    setState({ status: 'unauthenticated', accessToken: null, userId: null });
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ ...state, login, signup, consumeMagicLink, clearSession }),
    [state, login, signup, consumeMagicLink, clearSession],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an <AuthProvider>');
  return ctx;
}
